import { useEffect, useState } from 'react';
import { StyleSheet, useWindowDimensions, View } from 'react-native';
import App, { type AppNavigationSnapshot } from '../../mobile/App';
import { getStoredToken } from '../../mobile/src/auth/session';
import { apiGet } from '../../mobile/src/api/client';
import { buildPath, parsePath, type WebRoute } from './routes';

export function WebApp() {
  useWindowDimensions();
  const [webRoute, setWebRoute] = useState<WebRoute>(() => parsePath(window.location.pathname));
  const [profileData, setProfileData] = useState<any>();
  const [profileLoading, setProfileLoading] = useState(false);
  const [coarsePointer, setCoarsePointer] = useState(() => window.matchMedia?.('(pointer: coarse)').matches ?? false);
  const mobileViewport = coarsePointer || /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);

  useEffect(() => {
    const onPopState = () => setWebRoute(parsePath(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  useEffect(() => {
    const query = window.matchMedia?.('(pointer: coarse)');
    if (!query) {
      return;
    }
    const updatePointer = () => setCoarsePointer(query.matches);
    updatePointer();
    query.addEventListener?.('change', updatePointer);
    return () => query.removeEventListener?.('change', updatePointer);
  }, []);

  useEffect(() => {
    if (webRoute.kind !== 'profile') {
      setProfileData(undefined);
      setProfileLoading(false);
      return;
    }

    const token = getStoredToken();
    if (!token) {
      setProfileData(null);
      setProfileLoading(false);
      return;
    }

    let cancelled = false;
    setProfileLoading(true);
    void apiGet(`/users/by-handle/${encodeURIComponent(webRoute.handle)}/profile`, token)
      .then(profile => {
        if (!cancelled) {
          setProfileData(profile);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProfileData(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setProfileLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [webRoute]);

  function handleNavigate(snapshot: AppNavigationSnapshot) {
    const nextPath = buildPath(snapshot);
    if (nextPath !== window.location.pathname) {
      window.history.pushState({}, '', nextPath);
      setWebRoute(parsePath(nextPath));
    }
  }

  return (
    <View style={[styles.page, mobileViewport && styles.mobilePage]}>
      <View style={[styles.frame, mobileViewport && styles.mobileFrame]}>
        <App
          webRoute={webRoute}
          profileRouteData={profileData}
          profileRouteLoading={profileLoading}
          onNavigate={handleNavigate}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  page: {
    flex: 1,
    width: '100%',
    height: '100vh',
    maxHeight: '100vh',
    overflow: 'hidden',
    backgroundColor: '#ececec',
    alignItems: 'center',
  },
  frame: {
    flex: 1,
    width: '100%',
    maxWidth: 480,
    height: '100%',
    maxHeight: '100vh',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    shadowColor: '#000000',
    shadowOpacity: 0.08,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
  },
  mobilePage: {
    height: 'auto',
    minHeight: '100vh',
    maxHeight: undefined,
    overflow: 'visible',
    alignItems: 'stretch',
  },
  mobileFrame: {
    maxWidth: undefined,
    height: 'auto',
    minHeight: '100vh',
    maxHeight: undefined,
    overflow: 'visible',
    backgroundColor: '#ffffff',
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
  },
});
